/**
 * =============================================================================
 * ALERT DETAIL LAYOUT COMPONENT
 * =============================================================================
 *
 * 3-pane layout when viewing an alert:
 * - Center: Alert flow (pinned card, drill-down tables, action logs, action cards)
 * - Right: Chat input panel for asking AI questions (calls Mastra endpoint)
 *
 * DRILL-DOWN LOGIC:
 * Two paths, used together:
 *
 * PATH A — Alert-scope supporting data (rawSupportingData):
 *   Steps without $ctx.selectedEntity run at evaluation time and are stored in the
 *   checklist item. resolveAllMatchingData finds matching arrays by entity key.
 *
 * PATH B — Entity-scope resolution (entitySupportingData):
 *   Steps with $ctx.selectedEntity run on demand when an entity is clicked.
 *   Called via POST /check-list-items/resolve-entity/:id.
 *   Clickability is determined by alert.entityLinkedFields (parsed from the generator
 *   config by api.ts — any $ctx.selectedEntity.<field> across all steps).
 *   Works at L1 (breaching entity click) and L2+ (drill-down row click).
 */

import { Box, Stack, Text, ActionIcon, Group, ScrollArea, Divider, Modal, Button, Tooltip } from '@mantine/core';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import AskAiButton from '@components/QuickHelpPanel/AskAiButton';
import classes from './AlertDetailLayout.module.css';
import { ArrowLeft, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { getMerchantId } from '@api';
import {
    fetchActionDetails,
    executeAlertAction,
    snoozeAlert,
    ignoreAlert,
    completeAlert,
    submitIframeEdit,
    fetchActivityLogs,
    createActivityLog,
    resolveEntityData,
    type ActivityLogDto,
} from './api';
import PinnedAlertCard from './PinnedAlertCard';
import ActionDetailCard from './ActionDetailCard';
import ChatLogEntry from './ChatLogEntry';
import IframeEditModal from './IframeEditModal';

import type { AlertItem, AlertAction, ActionDetails, ChatMessage } from './types';
import { ChatLogType, SNOOZE_OPTIONS, AlertStatus } from './types';
import {
    resolveAllMatchingData,
    extractNestedDataFromItem,
    formatLabel,
    extractTimeSeriesEntries,
    isTimeSeriesResponse,
    type DrillDownConfig,
} from './utils/pattern-resolver';
import { TrendChart } from './TrendChart';
import { formatCount } from '@utils/currency';

interface AlertDetailLayoutProps {
    alert: AlertItem;
    onBack: () => void;
    onAlertUpdated: (alertId: string, action: 'deleted' | 'completed' | 'snoozed' | 'ignored') => void;
    currentUser?: { name: string };
}

/** Represents a level of drill-down data to display */
interface DrillDownLevel {
    title: string;
    data: Record<string, unknown>[];
    selectedIndex?: number;
    clickableKey?: string;
    /** The array name in supportingData this came from */
    sourceArrayName?: string;
    /** Whether items in this level have nested data */
    hasNestedData?: boolean;
    /** The linking key used to filter/connect to sibling arrays */
    linkingKey?: string;
}

const generateId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const LEGACY_PO_PREVIEW_LOG_PATTERN = /^(loaded|failed to load)\s+po\s+.+\s+line items$/i;

// Keys to skip when rendering table columns. merchantId is an internal
// identifier that must never surface in the UI.
const SKIP_KEYS = ['groupings', 'items', 'consumption_per_day', 'consumption_per_month', 'months_on_hand', 'merchantId', 'merchant_id'];

// =============================================================================
// HELPER: Local utilities for data display
// =============================================================================

// Matches "2026-04-27" and full ISO timestamps like "2026-04-27T18:30:00.000Z"
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Format a value for display
 */
const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
        return Number.isInteger(value)
            ? formatCount(value)
            : value.toFixed(2);
    }
    if (typeof value === 'string' && ISO_DATE_PATTERN.test(value)) {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return format(parsed, 'dd MMM yyyy');
        }
    }
    return String(value);
};

/**
 * Get table columns from data (first-level primitives only)
 */
const getColumnsFromData = (data: Record<string, unknown>[]): string[] => {
    if (data.length === 0) return [];

    const firstItem = data[0];
    const cols = Object.keys(firstItem).filter((key) => {
        if (SKIP_KEYS.includes(key)) return false;
        const value = firstItem[key];
        return typeof value !== 'object' || value === null;
    }).slice(0, 8); // Max 8 columns

    // Move 'label' to front so the identifier column is always first
    const labelIdx = cols.indexOf('label');
    if (labelIdx > 0) {
        cols.unshift(cols.splice(labelIdx, 1)[0]);
    }

    return cols;
};

/**
 * Check if a column has nested data that can be drilled into
 */
const hasNestedDataForKey = (
    data: Record<string, unknown>[],
    key: string,
): boolean => {
    if (data.length === 0) return false;
    const firstItem = data[0];
    
    // Check if any value in the item is an array or object
    for (const [k, v] of Object.entries(firstItem)) {
        if (k === key) continue;
        if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') return true;
        if (typeof v === 'object' && v !== null && !Array.isArray(v)) return true;
    }
    return false;
};

/**
 * Build drill-down levels from entity-scope resolution output.
 *
 * For each key in the resolved data:
 *   - TIME_SERIES  → skip (rendered separately as TrendChart)
 *   - Array of objects → add as a DataTable drill-down level
 *   - Everything else  → skip (config objects, scalars, etc.)
 *
 * Row clickability within each table is determined by entityLinkedFields:
 * if the table rows have a field that's in entityLinkedFields, that field
 * is the clickable key (meaning clicking that row will trigger another
 * resolve-entity call for that sub-entity).
 */
const buildDrillDownFromEntityData = (
    entityData: Record<string, unknown>,
    entityLinkedFields?: string[],
): DrillDownLevel[] => {
    const levels: DrillDownLevel[] = [];

    for (const [key, value] of Object.entries(entityData)) {
        if (key.startsWith('_')) continue;
        if (isTimeSeriesResponse(value)) continue;
        if (!Array.isArray(value) || value.length === 0) continue;
        if (typeof value[0] !== 'object' || value[0] === null) continue;

        const data = value as Record<string, unknown>[];
        const sampleRow = data[0];

        // Identify nested array keys (sub-arrays of objects) in each row
        const nestedArrayKeys = Object.keys(sampleRow).filter((k) => {
            const v = sampleRow[k];
            return Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && v[0] !== null;
        });

        // Build flat rows (strip nested arrays so they don't render as [object Object])
        const flatData = data.map((row) => {
            const flat: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(row)) {
                if (!Array.isArray(v)) flat[k] = v;
            }
            return flat;
        });

        const clickableKey = entityLinkedFields?.find((f) => f in sampleRow) ?? undefined;

        levels.push({
            title: formatLabel(key),
            data: flatData,
            selectedIndex: undefined,
            clickableKey,
            sourceArrayName: key,
            hasNestedData: nestedArrayKeys.length > 0,
            linkingKey: clickableKey,
        });

        // Add a separate DrillDownLevel for each nested array (flattened across all parent rows)
        for (const nestedKey of nestedArrayKeys) {
            const nestedRows: Record<string, unknown>[] = [];
            for (const row of data) {
                const nested = row[nestedKey];
                if (Array.isArray(nested)) {
                    nestedRows.push(...(nested as Record<string, unknown>[]));
                }
            }
            if (nestedRows.length > 0) {
                const nestedClickableKey = entityLinkedFields?.find((f) => f in nestedRows[0]) ?? undefined;
                levels.push({
                    title: formatLabel(nestedKey),
                    data: nestedRows,
                    selectedIndex: undefined,
                    clickableKey: nestedClickableKey,
                    sourceArrayName: nestedKey,
                    hasNestedData: false,
                    linkingKey: nestedClickableKey,
                });
            }
        }
    }

    return levels;
};

// =============================================================================
// SUB-COMPONENT: Generic Data Table
// =============================================================================

interface DataTableProps {
    title: string;
    data: Record<string, unknown>[];
    onRowClick?: (key: string, value: string, index: number) => void;
    selectedIndex?: number;
    clickableKey?: string;
    /** Whether this table's items have nested data that can be drilled into */
    hasNestedData?: boolean;
    /** The source array name for this data (used to check sibling arrays) */
    sourceArrayName?: string;
    /** Full supportingData for checking sibling arrays */
    supportingData?: Record<string, unknown>;
    /** All source array names currently being displayed (to exclude from clickable detection) */
    displayedSourceArrayNames?: string[];
}

const DataTable: React.FC<DataTableProps> = memo(({
    title,
    data,
    onRowClick,
    selectedIndex,
    clickableKey,
    hasNestedData,
    sourceArrayName,
    supportingData,
    displayedSourceArrayNames,
}) => {
    const columns = useMemo(() => getColumnsFromData(data), [data]);
    
    // Auto-detect clickable key based on:
    // 1. Item has nested data (arrays or identifier-keyed objects) that can be drilled into
    // 2. OR there's deeper drill-down data available in supportingData for this entity
    // 
    // IMPORTANT: A column is only clickable if there's ACTUALLY more data to show.
    // Leaf nodes (no further drill-down) should NOT be clickable.
    const effectiveClickableKey = useMemo(() => {
        // If parent explicitly set a clickable key, use it (already validated)
        if (clickableKey) return clickableKey;
        if (data.length === 0) return null;
        
        const firstRow = data[0];
        
        // Helper to check if a column value is a valid identifier (string, not numeric)
        const isValidIdentifierColumn = (col: string): boolean => {
            const value = firstRow[col];
            // Skip numeric values - they're not identifiers
            if (typeof value === 'number') return false;
            // Skip null/undefined
            if (value === null || value === undefined) return false;
            // Skip if the string is purely numeric
            if (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '') return false;
            return true;
        };
        
        // Check if this table's items have nested data (arrays or identifier-keyed objects)
        const tableHasNestedData = hasNestedData || data.some(row => {
            for (const [, v] of Object.entries(row)) {
                // Nested arrays with objects
                if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') return true;
                // Identifier-keyed objects (like groupings: { 'WH-1': {...}, 'WH-2': {...} })
                if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
                    const entries = Object.entries(v);
                    if (entries.length > 0 && typeof entries[0][1] === 'object') return true;
                }
            }
            return false;
        });
        
        // Check each column for clickability
        for (const col of columns) {
            if (!isValidIdentifierColumn(col)) continue;
            
            const colValue = firstRow[col];
            if (colValue === null || colValue === undefined) continue;
            const colValueStr = String(colValue);
            
            // Condition 1: This table's items have nested data that can be drilled into
            if (tableHasNestedData) {
                return col;
            }
            
            // Condition 2: Check if there's deeper drill-down data in supportingData
            // Use resolveAllMatchingData for consistency - same logic as what would actually be shown
            // Exclude all currently displayed sources to avoid showing data that's already visible
            if (supportingData && displayedSourceArrayNames) {
                const potentialConfigs = resolveAllMatchingData(
                    supportingData as Record<string, unknown>,
                    col,
                    colValueStr,
                );
                // Check if any configs would be NEW (not already displayed or nested under displayed)
                const hasNewData = potentialConfigs.some(pc => {
                    const isDisplayed = displayedSourceArrayNames.some(displayed =>
                        pc.sourceArrayName === displayed ||
                        pc.sourceArrayName.startsWith(displayed + '_')
                    );
                    return !isDisplayed;
                });
                if (hasNewData) {
                    return col;
                }
            }
        }
        
        // No clickable key found - this is a leaf node
        return null;
    }, [clickableKey, columns, data, hasNestedData, supportingData, sourceArrayName, displayedSourceArrayNames]);
    
    // NOTE: No auto-click for drill-down tables - this prevents infinite cascading
    // when clicking entities. Auto-click is only done in BreachingEntitiesTable (top-level).
    
    const sheetColumns = useMemo((): DataSheetColumn<Record<string, unknown>>[] =>
        columns.map((col) => ({
            key: col,
            header: formatLabel(col),
            align: data.length > 0 && typeof data[0][col] === 'number' ? ('right' as const) : ('left' as const),
            render: (value: unknown) => {
                if (col === effectiveClickableKey && onRowClick) {
                    const str = value !== null && value !== undefined ? String(value) : '';
                    if (!str) return <>{formatValue(value)}</>;
                    return <span className={classes.linkCell}>{str}</span>;
                }
                const formatted = formatValue(value);
                if (formatted.length < 30) return <>{formatted}</>;
                return (
                    <Tooltip label={formatted} withArrow>
                        <span>{formatted.slice(0, 29)}…</span>
                    </Tooltip>
                );
            },
        })),
        [columns, data, effectiveClickableKey, onRowClick],
    );

    const handleRowClick = useCallback(
        (row: Record<string, unknown>, rowIndex: number) => {
            if (!effectiveClickableKey || !onRowClick) return;
            const value = String(row[effectiveClickableKey] ?? '');
            if (value) onRowClick(effectiveClickableKey, value, rowIndex);
        },
        [effectiveClickableKey, onRowClick],
    );

    if (data.length === 0 || columns.length === 0) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
        >
            <DataSheet
                title={`${title} (${data.length})`}
                data={data}
                columns={sheetColumns}
                onRowClick={effectiveClickableKey ? handleRowClick : undefined}
                getRowClassName={(_, rowIndex) => (rowIndex === selectedIndex ? classes.selectedRow : '')}
                maxHeight={280}
            />
        </motion.div>
    );
});

DataTable.displayName = 'DataTable';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const AlertDetailLayout: React.FC<AlertDetailLayoutProps> = memo(({
    alert,
    onBack,
    onAlertUpdated,
    currentUser = { name: 'You' },
}) => {
    // =========================================================================
    // STATE - Alert Actions
    // =========================================================================
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pendingAction, setPendingAction] = useState<AlertAction | null>(null);
    const [pendingActionDetails, setPendingActionDetails] = useState<ActionDetails | null>(null);
    const [iframeAction, setIframeAction] = useState<AlertAction | null>(null);
    const [iframeUrl, setIframeUrl] = useState<string | null>(null);
    const [iframeActionDetails, setIframeActionDetails] = useState<ActionDetails | null>(null);
    
    // =========================================================================
    // STATE - Generic Drill-Down (using rawSupportingData)
    // =========================================================================
    const [selectedEntityIndex, setSelectedEntityIndex] = useState<number | undefined>(undefined);
    const [drillDownLevels, setDrillDownLevels] = useState<DrillDownLevel[]>([]);

    // =========================================================================
    // STATE - Entity-scope supporting data (lazy-loaded per selected entity)
    // =========================================================================
    const [entitySupportingData, setEntitySupportingData] = useState<Record<string, unknown> | null>(null);
    const [entityDataLoading, setEntityDataLoading] = useState(false);
    
    // =========================================================================
    // STATE - Modals
    // =========================================================================
    const [showIgnoreModal, setShowIgnoreModal] = useState(false);
    
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    // Clear state when alert changes
    useEffect(() => {
        setSelectedEntityIndex(undefined);
        setDrillDownLevels([]);
        setEntitySupportingData(null);
    }, [alert.id]);

    // When entitySupportingData arrives (from any entity click at any drill-down level),
    // build drill-down levels from it. TIME_SERIES entries are excluded here because
    // they are rendered separately as TrendChart components in the render section.
    useEffect(() => {
        if (!entitySupportingData || Object.keys(entitySupportingData).length === 0) return;
        const levels = buildDrillDownFromEntityData(entitySupportingData, alert.entityLinkedFields);
        setDrillDownLevels(levels);
    }, [entitySupportingData, alert.entityLinkedFields]);

    // =========================================================================
    // FETCH ACTIVITY LOGS ON MOUNT
    // =========================================================================
    useEffect(() => {
        const loadActivityLogs = async () => {
            try {
                const logs = await fetchActivityLogs(alert.id);
                const visibleLogs = logs.filter(
                    (log) => !LEGACY_PO_PREVIEW_LOG_PATTERN.test(log.logDetails.trim()),
                );
                
                const chatMessages: ChatMessage[] = visibleLogs.map((log: ActivityLogDto) => ({
                    id: log.logId,
                    type: 'log' as const,
                    logType: (log.logType as ChatLogType) || ChatLogType.SYSTEM,
                    message: log.logDetails,
                    user: log.userName || 'System',
                    timestamp: log.createdAt,
                }));
                
                setChatMessages(chatMessages);
            } catch (error) {
                console.error('[AlertDetail] Failed to load activity logs:', error);
            }
        };
        
        loadActivityLogs();
    }, [alert.id]);

    // =========================================================================
    // LOG HELPERS
    // =========================================================================
    const addLog = useCallback(async (logType: ChatLogType, message: string) => {
        const tempId = generateId();
        const timestamp = new Date().toISOString();
        
        const logMessage: ChatMessage = {
            id: tempId,
            type: 'log',
            logType,
            message,
            user: currentUser.name,
            timestamp,
        };
        setChatMessages((prev) => [...prev, logMessage]);
        
        createActivityLog(alert.id, message, logType, currentUser.name)
            .then((savedLog) => {
                if (savedLog) {
                    setChatMessages((prev) => 
                        prev.map((msg) => 
                            msg.id === tempId 
                                ? { ...msg, id: savedLog.logId }
                                : msg
                        )
                    );
                }
            })
            .catch((error) => {
                console.error('[AlertDetail] Failed to persist log:', error);
            });
    }, [currentUser.name, alert.id]);

    // =========================================================================
    // GENERIC ENTITY CLICK HANDLER
    // PATH B: entity-scope — calls resolve-entity, useEffect populates drillDownLevels
    // PATH A: alert-scope rawSupportingData fallback (legacy, kept for backward compat)
    // =========================================================================
    const handleEntityClick = useCallback((key: string, value: string, entityIndex: number) => {
        setSelectedEntityIndex(entityIndex);
        setDrillDownLevels([]); // clear previous entity's data immediately
        setEntitySupportingData(null);

        // PATH B: resolve entity-scope data (async)
        // useEffect below watches entitySupportingData and populates drillDownLevels
        const clickedEntity = alert.breachingEntities?.[entityIndex];
        if (clickedEntity) {
            setEntityDataLoading(true);
            getMerchantId()
                .then((merchantId) => {
                    if (!merchantId) return;
                    return resolveEntityData(alert.id, merchantId, clickedEntity as Record<string, unknown>);
                })
                .then((data) => {
                    if (data && Object.keys(data).length > 0) setEntitySupportingData(data);
                })
                .finally(() => setEntityDataLoading(false));
        }

        // PATH A: if alert-scope rawSupportingData exists, also resolve from it synchronously
        const rawSupportingData = alert.rawSupportingData;
        const hasAlertScopeArrays = rawSupportingData
            ? Object.keys(rawSupportingData).some(k => !k.startsWith('_') && Array.isArray(rawSupportingData[k]))
            : false;

        if (!hasAlertScopeArrays) {
            // No alert-scope arrays — PATH B useEffect will handle drill-down
            return;
        }
        
        // Find ALL matching data from ALL arrays in supportingData
        // This now returns BOTH L1 data AND nested content (groupings, arrays) for each pattern
        const drillDownConfigs = resolveAllMatchingData(rawSupportingData!, key, value);
        console.log('[handleEntityClick] drillDownConfigs:', drillDownConfigs.map(c => ({
            title: c.title,
            dataLength: c.data.length,
            hasNestedData: c.hasNestedData,
        })));
        
        // Convert DrillDownConfig[] to DrillDownLevel[] for state
        // Determine clickable key for each level based on deeper drill-down availability
        const levels: DrillDownLevel[] = [];
        const processedSourceArrays = new Set<string>();
        
        // Collect ALL source array names being displayed - these should be excluded
        // when checking for deeper drill-down (since their data is already shown)
        const allDisplayedSources = drillDownConfigs.map(c => c.sourceArrayName);
        
        for (const config of drillDownConfigs) {
            // Skip if we've already processed this source (avoid duplicates)
            if (processedSourceArrays.has(config.sourceArrayName)) continue;
            processedSourceArrays.add(config.sourceArrayName);
            
            // Determine if this level should have a clickable key
            // Only make clickable if clicking would reveal NEW data not already displayed
            // Use resolveAllMatchingData to check (same logic as what would actually be shown)
            let effectiveClickableKey: string | undefined;
            if (config.linkingKey && rawSupportingData && config.data.length > 0) {
                const sampleValue = config.data[0][config.linkingKey];
                if (sampleValue !== undefined && sampleValue !== null) {
                    // Check what would be returned if we clicked this value
                    const potentialConfigs = resolveAllMatchingData(
                        rawSupportingData,
                        config.linkingKey,
                        String(sampleValue),
                    );
                    // Filter out configs that are already displayed OR are nested under displayed sources
                    // e.g., if "internal_stock" is displayed, also filter out "internal_stock_groupings"
                    const newConfigs = potentialConfigs.filter(pc => {
                        // Check if this config or its parent is already displayed
                        const isDisplayed = allDisplayedSources.some(displayed =>
                            pc.sourceArrayName === displayed ||
                            pc.sourceArrayName.startsWith(displayed + '_')
                        );
                        return !isDisplayed;
                    });
                    // Only make clickable if there would be NEW data to show
                    if (newConfigs.length > 0) {
                        effectiveClickableKey = config.linkingKey;
                    }
                }
            }
            
            levels.push({
                title: config.title,
                data: config.data,
                selectedIndex: undefined,
                clickableKey: effectiveClickableKey,
                sourceArrayName: config.sourceArrayName,
                hasNestedData: config.hasNestedData,
                linkingKey: config.linkingKey,
            });
        }
        
        console.log('[handleEntityClick] Final levels:', levels.map(l => ({
            title: l.title,
            dataLength: l.data.length,
            clickableKey: l.clickableKey,
        })));
        
        setDrillDownLevels(levels);
    }, [alert.rawSupportingData, alert.breachingEntities, alert.id]);

    // =========================================================================
    // DRILL-DOWN TABLE CLICK HANDLERS
    // =========================================================================
    const handleDrillDownClick = useCallback((levelIndex: number, key: string, value: string, rowIndex: number) => {
        const rawSupportingData = alert.rawSupportingData;

        // PATH B: if the clicked key is entity-linked, resolve it via the backend pipeline.
        // This handles L2, L3, ... — same resolve-entity endpoint, null-param skip does
        // field-level filtering so only steps that need this entity's fields run.
        if (alert.entityLinkedFields?.includes(key)) {
            const clickedRow = drillDownLevels[levelIndex]?.data[rowIndex];
            if (clickedRow) {
                // Mark the clicked row as selected and truncate deeper levels
                setDrillDownLevels(prev => prev.slice(0, levelIndex + 1).map((l, i) =>
                    i === levelIndex ? { ...l, selectedIndex: rowIndex } : l,
                ));
                setEntitySupportingData(null);
                setEntityDataLoading(true);
                getMerchantId()
                    .then(merchantId => {
                        if (!merchantId) return;
                        return resolveEntityData(alert.id, merchantId, clickedRow);
                    })
                    .then(data => {
                        if (data && Object.keys(data).length > 0) {
                            // Append new levels below the current selection
                            const newLevels = buildDrillDownFromEntityData(data, alert.entityLinkedFields);
                            setDrillDownLevels(prev => [...prev, ...newLevels]);
                            setEntitySupportingData(data);
                        }
                    })
                    .finally(() => setEntityDataLoading(false));
            }
            return;
        }

        console.log('[handleDrillDownClick] Called:', { levelIndex, key, value, rowIndex });
        
        setDrillDownLevels((prev) => {
            const currentLevel = prev[levelIndex];
            
            // Update selection on this level
            const truncated = prev.slice(0, levelIndex + 1);
            truncated[levelIndex] = {
                ...currentLevel,
                selectedIndex: rowIndex,
                clickableKey: key,
            };
            
            // Get clicked item
            const clickedItem = currentLevel.data[rowIndex];
            if (!clickedItem) return truncated;
            
            const processedSourceArrays = new Set<string>(truncated.map(l => l.sourceArrayName || ''));
            
            // Helper to determine clickable key for a config
            // Uses resolveAllMatchingData for consistency - same logic as what would actually be shown
            const getEffectiveClickableKey = (config: DrillDownConfig, allSources: string[]): string | undefined => {
                if (!config.linkingKey || !rawSupportingData) return undefined;
                const sampleValue = config.data[0]?.[config.linkingKey];
                if (sampleValue === undefined || sampleValue === null) return undefined;
                
                // Check what would be returned if we clicked this value
                const potentialConfigs = resolveAllMatchingData(
                    rawSupportingData,
                    config.linkingKey,
                    String(sampleValue),
                );
                // Only make clickable if there would be NEW data (not already displayed or nested under displayed)
                const hasNewData = potentialConfigs.some(pc => {
                    const isDisplayed = allSources.some(displayed =>
                        pc.sourceArrayName === displayed ||
                        pc.sourceArrayName.startsWith(displayed + '_')
                    );
                    return !isDisplayed;
                });
                return hasNewData ? config.linkingKey : undefined;
            };
            
            // CASE 1: Extract nested data from clicked item
            const nestedData = extractNestedDataFromItem(clickedItem, value);
            nestedData.forEach((config) => {
                if (processedSourceArrays.has(config.sourceArrayName)) return;
                processedSourceArrays.add(config.sourceArrayName);
                
                truncated.push({
                    title: config.title,
                    data: config.data,
                    selectedIndex: undefined,
                    clickableKey: undefined, // Will be determined after all configs are collected
                    sourceArrayName: config.sourceArrayName,
                    hasNestedData: config.hasNestedData,
                    linkingKey: config.linkingKey,
                });
            });
            
            // CASE 2: Find ALL matching data in supportingData (including nested content)
            // Use resolveAllMatchingData to get L1 + nested groupings/arrays together
            if (rawSupportingData) {
                const matchingConfigs = resolveAllMatchingData(
                    rawSupportingData,
                    key,
                    value,
                );
                
                matchingConfigs.forEach((config) => {
                    if (processedSourceArrays.has(config.sourceArrayName)) return;
                    processedSourceArrays.add(config.sourceArrayName);
                    
                    truncated.push({
                        title: config.title,
                        data: config.data,
                        selectedIndex: undefined,
                        clickableKey: undefined, // Will be determined after all configs are collected
                        sourceArrayName: config.sourceArrayName,
                        hasNestedData: config.hasNestedData,
                        linkingKey: config.linkingKey,
                    });
                });
            }
            
            // Now determine clickable keys for all levels using the full list of sources
            const allSources = Array.from(processedSourceArrays);
            return truncated.map(level => ({
                ...level,
                clickableKey: level.clickableKey || getEffectiveClickableKey(
                    { ...level, linkingKey: level.linkingKey } as DrillDownConfig,
                    allSources,
                ),
            }));
        });
    }, [alert.rawSupportingData]);

    // =========================================================================
    // ACTION HANDLERS
    // =========================================================================
    const handleActionClick = useCallback(async (action: AlertAction) => {
        addLog(ChatLogType.ACTION_SELECTED, `Selected action: ${action.label}`);
        setIsLoading(true);
        try {
            const details = await fetchActionDetails(alert.id, action.id);
            setPendingAction(action);
            setPendingActionDetails(details);
        } catch (error) {
            console.error('Failed to fetch action details:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to load action details. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [alert.id, addLog]);

    const handleConfirmAction = useCallback(async () => {
        if (!pendingAction || !pendingActionDetails) return;
        addLog(ChatLogType.ACTION_CONFIRMED, `Confirmed: ${pendingAction.label}`);
        setIsLoading(true);
        try {
            const result = await executeAlertAction(alert.id, pendingAction);
            const resultMessage: ChatMessage = {
                id: generateId(),
                type: 'action_result',
                action: pendingAction,
                success: result.success,
                message: result.message,
                timestamp: new Date().toISOString(),
            };
            setChatMessages((prev) => [...prev, resultMessage]);
            if (result.success) {
                onAlertUpdated(alert.id, 'completed');
            }
        } catch (error) {
            console.error('Failed to execute action:', error);
            addLog(ChatLogType.SYSTEM, 'Action failed. Please try again.');
        } finally {
            setIsLoading(false);
            setPendingAction(null);
            setPendingActionDetails(null);
        }
    }, [pendingAction, pendingActionDetails, alert.id, addLog, onAlertUpdated]);

    const handleRejectAction = useCallback(() => {
        addLog(ChatLogType.ACTION_REJECTED, `Cancelled: ${pendingAction?.label}`);
        setPendingAction(null);
        setPendingActionDetails(null);
    }, [pendingAction, addLog]);

    const handleEditAction = useCallback(() => {
        if (!pendingAction || !pendingActionDetails) return;
        addLog(ChatLogType.EDIT_STARTED, `Editing: ${pendingAction.label}`);
        setIframeAction(pendingAction);
        setIframeActionDetails(pendingActionDetails);
        setIframeUrl(pendingActionDetails.editUrl || `/edit/action/${alert.id}/${pendingAction.id}`);
        setPendingAction(null);
        setPendingActionDetails(null);
    }, [pendingAction, pendingActionDetails, alert.id, addLog]);

    const handleSnooze = useCallback(async (minutes: number) => {
        const option = SNOOZE_OPTIONS.find(o => o.value === minutes);
        addLog(ChatLogType.ALERT_SNOOZED, `Snoozing for ${option?.label || minutes + ' minutes'}`);
        setIsLoading(true);
        try {
            const result = await snoozeAlert(alert.id, minutes);
            if (result.success) {
                addLog(ChatLogType.SYSTEM, result.message);
                onAlertUpdated(alert.id, 'snoozed');
            }
        } catch (error) {
            console.error('Failed to snooze:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to snooze. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [alert.id, addLog, onAlertUpdated]);

    const handleIgnoreConfirm = useCallback(async () => {
        setShowIgnoreModal(false);
        addLog(ChatLogType.SYSTEM, 'Ignoring alert...');
        setIsLoading(true);
        try {
            const result = await ignoreAlert(alert.id);
            if (result.success) {
                addLog(ChatLogType.SYSTEM, 'Alert ignored');
                onAlertUpdated(alert.id, 'deleted');
                onBack();
            }
        } catch (error) {
            console.error('Failed to ignore:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to ignore. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [alert.id, addLog, onAlertUpdated, onBack]);

    const handleComplete = useCallback(async () => {
        addLog(ChatLogType.SYSTEM, 'Marking alert as complete...');
        setIsLoading(true);
        try {
            const result = await completeAlert(alert.id);
            if (result.success) {
                addLog(ChatLogType.ACTION_CONFIRMED, 'Alert marked as complete');
                onAlertUpdated(alert.id, 'completed');
            }
        } catch (error) {
            console.error('Failed to complete:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to complete. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [alert.id, addLog, onAlertUpdated]);

    // =========================================================================
    // COMPUTED
    // =========================================================================
    const isAlertDone = alert.status === AlertStatus.COMPLETED || 
                        alert.status === AlertStatus.CANCELLED;

    const handleIframeConfirm = useCallback(async (editedData: Record<string, unknown>) => {
        if (!iframeAction) return;
        addLog(ChatLogType.EDIT_COMPLETED, `Edited and confirmed: ${iframeAction.label}`);
        setIsLoading(true);
        try {
            const result = await submitIframeEdit(alert.id, iframeAction, editedData);
            const resultMessage: ChatMessage = {
                id: generateId(),
                type: 'action_result',
                action: iframeAction,
                success: result.success,
                message: result.message,
                timestamp: new Date().toISOString(),
            };
            setChatMessages((prev) => [...prev, resultMessage]);
            if (result.success) {
                onAlertUpdated(alert.id, 'completed');
            }
        } catch (error) {
            console.error('Failed to submit edit:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to save changes. Please try again.');
        } finally {
            setIsLoading(false);
            setIframeAction(null);
            setIframeUrl(null);
            setIframeActionDetails(null);
        }
    }, [iframeAction, alert.id, addLog, onAlertUpdated]);

    const handleIframeCancel = useCallback(() => {
        addLog(ChatLogType.EDIT_CANCELLED, 'Edit cancelled');
        setIframeAction(null);
        setIframeUrl(null);
        setIframeActionDetails(null);
    }, [addLog]);

    // =========================================================================
    // RENDER
    // =========================================================================
    return (
        <Box className="flex h-full w-full overflow-hidden">
            {/* CENTER PANE: Alert Flow & Logs */}
            <Box className="flex-1 flex flex-col h-full min-w-0">
                {/* Header with 3-dot menu - AI-Native Design System */}
                <Box 
                    className="sticky top-0 z-10 flex-shrink-0"
                    style={{ 
                        backgroundColor: 'white',
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                        padding: '16px 24px',
                    }}
                >
                    <Group className="justify-between">
                        <Group style={{ gap: 16, flex: 1, minWidth: 0 }}>
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="md"
                                onClick={onBack}
                            >
                                <ArrowLeft size={18} weight="bold" />
                            </ActionIcon>
                            <Stack style={{ gap: 2, flex: 1, minWidth: 0 }}>
                                {/* Alert type label - 10px uppercase */}
                                <Text 
                                    style={{ 
                                        fontSize: 10, 
                                        fontWeight: 600, 
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                        color: 'rgb(148, 163, 184)' 
                                    }}
                                >
                                    {alert.alertTypeLabel}
                                </Text>
                                {/* Alert title - 18px section header */}
                                <Text 
                                    className="truncate"
                                    style={{ 
                                        fontSize: 18, 
                                        fontWeight: 600, 
                                        lineHeight: 1.3,
                                        color: 'rgb(26, 32, 44)' 
                                    }}
                                >
                                    {alert.title}
                                </Text>
                            </Stack>
                        </Group>

                        <AskAiButton />
                    </Group>
                </Box>

                {/* Alert Content */}
                <ScrollArea className="flex-1" scrollbarSize={6}>
                    <Stack className="gap-4 p-4">
                        {/* Pinned Alert Card */}
                        <PinnedAlertCard
                            alert={alert}
                            onActionClick={handleActionClick}
                            isLoading={isLoading}
                            disableActions={!!pendingAction || isAlertDone}
                            onEntityClick={handleEntityClick}
                            selectedEntityIndex={selectedEntityIndex}
                            isAlertDone={isAlertDone}
                            onComplete={handleComplete}
                            onSnooze={handleSnooze}
                            onShowIgnoreModal={() => setShowIgnoreModal(true)}
                        />
                        
                        {/* Show read-only notice for done alerts */}
                        {isAlertDone && (
                            <Box className="rounded-lg bg-green-50 border border-green-200 p-3 flex items-center gap-2">
                                <CheckCircle size={18} weight="fill" className="text-green-600" />
                                <Text className="text-sm text-green-700 font-medium">
                                    This alert has been completed. You can view the activity history below.
                                </Text>
                            </Box>
                        )}

                        <Divider label="Supporting Data" labelPosition="center" className="my-2" />

                        {/* TIME_SERIES Charts
                            Priority: entity-scope data (specific to clicked entity) when
                            an entity is selected; otherwise alert-scope rawSupportingData. */}
                        {extractTimeSeriesEntries(
                            (selectedEntityIndex !== undefined && entitySupportingData
                                ? entitySupportingData
                                : (alert.rawSupportingData ?? {})) as Record<string, unknown>
                        ).map(({ name, response }) => (
                            <TrendChart key={name} name={name} response={response} />
                        ))}

                        {/* Entity data loading indicator */}
                        {entityDataLoading && (
                            <Box className="flex justify-center py-3">
                                <Text className="text-xs text-gray-400 animate-pulse">Loading data…</Text>
                            </Box>
                        )}

                        {/* Generic Drill-Down Tables */}
                        {drillDownLevels.map((level, levelIndex) => (
                            <DataTable
                                key={`${level.title}-${levelIndex}`}
                                title={level.title}
                                data={level.data}
                                onRowClick={(key, value, rowIndex) =>
                                    handleDrillDownClick(levelIndex, key, value, rowIndex)
                                }
                                selectedIndex={level.selectedIndex}
                                clickableKey={level.clickableKey}
                                hasNestedData={level.hasNestedData}
                                sourceArrayName={level.sourceArrayName}
                                supportingData={alert.rawSupportingData}
                                displayedSourceArrayNames={drillDownLevels.map(l => l.sourceArrayName).filter(Boolean) as string[]}
                            />
                        ))}

                        {/* No supporting data message — only show when not loading */}
                        {selectedEntityIndex !== undefined && drillDownLevels.length === 0 && !entityDataLoading && (
                            <Box className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-center">
                                <Text className="text-sm text-gray-500">
                                    No additional data available for this selection.
                                </Text>
                            </Box>
                        )}

                        <Divider label="Activity" labelPosition="center" className="my-2" />

                        {/* Chat Messages / Logs */}
                        <AnimatePresence mode="popLayout">
                            {chatMessages.map((msg) => {
                                if (msg.type === 'log') {
                                    return (
                                        <ChatLogEntry
                                            key={msg.id}
                                            logType={msg.logType}
                                            message={msg.message}
                                            user={msg.user}
                                            timestamp={msg.timestamp}
                                        />
                                    );
                                }
                                if (msg.type === 'action_result') {
                                    return (
                                        <motion.div
                                            key={msg.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`p-3 rounded-lg border ${
                                                msg.success
                                                    ? 'bg-green-50 border-green-200'
                                                    : 'bg-red-50 border-red-200'
                                            }`}
                                        >
                                            <Text className={`text-sm font-medium ${msg.success ? 'text-green-700' : 'text-red-700'}`}>
                                                {msg.success ? '✓' : '✗'} {msg.message}
                                            </Text>
                                        </motion.div>
                                    );
                                }
                                return null;
                            })}
                        </AnimatePresence>

                        {/* Pending Action Card */}
                        {pendingAction && pendingActionDetails && (
                            <ActionDetailCard
                                action={pendingAction}
                                details={pendingActionDetails}
                                onConfirm={handleConfirmAction}
                                onReject={handleRejectAction}
                                onEdit={pendingAction.isEditable ? handleEditAction : undefined}
                                isLoading={isLoading}
                            />
                        )}

                        <div ref={messagesEndRef} />
                    </Stack>
                </ScrollArea>
            </Box>


            {/* IGNORE CONFIRMATION MODAL */}
            <Modal
                opened={showIgnoreModal}
                onClose={() => setShowIgnoreModal(false)}
                title={
                    <Group className="gap-2">
                        <WarningCircle size={20} weight="fill" className="text-orange-500" />
                        <Text className="font-semibold">Ignore Alert</Text>
                    </Group>
                }
                centered
                size="sm"
            >
                <Stack className="gap-4">
                    <Text className="text-sm text-gray-700">
                        Are you sure you want to ignore this alert? It will be moved to the ignored list and won't appear in your pending alerts.
                    </Text>
                    <Group className="justify-end gap-3">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={() => setShowIgnoreModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="blue"
                            onClick={handleIgnoreConfirm}
                            loading={isLoading}
                        >
                            Yes, Ignore
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Iframe Edit Modal */}
            <IframeEditModal
                opened={!!iframeUrl}
                url={iframeUrl || ''}
                action={iframeAction}
                actionDetails={iframeActionDetails}
                alertId={alert.id}
                onConfirm={handleIframeConfirm}
                onCancel={handleIframeCancel}
                isLoading={isLoading}
            />
        </Box>
    );
});

AlertDetailLayout.displayName = 'AlertDetailLayout';

export default AlertDetailLayout;
